import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { CheckoutButton, SubscriptionDetailsButton } from "@clerk/clerk-react/experimental";
import api from "../api";

const checkoutAppearance = {
  variables: {
    colorPrimary: "#C45C4A",
    colorBackground: "#FFFEF9",
    colorText: "#1A1A1A",
    colorTextSecondary: "#6B6B6B",
    borderRadius: "0.75rem",
  },
};

export default function PricingPage({ embedded = false }) {
  const { isSignedIn } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [billing, setBilling] = useState(null);
  const [msg, setMsg] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");

  const refresh = useCallback(async () => {
    if (!isSignedIn) return;
    const [meData, billingData, mine] = await Promise.all([
      api.get("/api/auth/me").catch(() => null),
      api.get("/api/billing/status").catch(() => null),
      api.get("/api/promos/mine").catch(() => null),
    ]);
    if (meData) setMe(meData);
    if (billingData) setBilling(billingData);
    if (mine?.pending) {
      setPromo({
        ok: true,
        code: mine.pending.code,
        channel: mine.pending.channel,
        label: mine.pending.label,
        trial_days: mine.pending.trial_days,
        benefit: mine.pending.benefit,
        first_month_free:
          (mine.pending.benefit || "") === "first_month_free" &&
          (mine.pending.trial_days || 0) > 0,
        detail: `Code ${mine.pending.code} appliqué`,
        reserved: true,
      });
      setPromoInput(mine.pending.code || "");
    }
  }, [isSignedIn]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Prefill from ?promo=LINKEDIN30
  useEffect(() => {
    const fromUrl = (searchParams.get("promo") || "").trim();
    if (fromUrl) setPromoInput(fromUrl.toUpperCase());
  }, [searchParams]);

  // Auto-reserve when signed in + ?promo=
  useEffect(() => {
    if (!isSignedIn) return;
    const fromUrl = (searchParams.get("promo") || "").trim();
    if (!fromUrl || promo?.reserved) return;
    let cancelled = false;
    (async () => {
      setPromoBusy(true);
      setPromoError("");
      try {
        const res = await api.post("/api/promos/reserve", {
          code: fromUrl.toUpperCase(),
        });
        if (!cancelled) {
          setPromo(res);
          setPromoInput(res.code || fromUrl.toUpperCase());
          setMsg(res.detail || "Code promo appliqué.");
        }
      } catch (e) {
        if (!cancelled) setPromoError(e.message || "Code invalide");
      } finally {
        if (!cancelled) setPromoBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, searchParams, promo?.reserved]);

  // After Stripe/Clerk redirect (?checkout=1), force a local plan sync
  useEffect(() => {
    if (!isSignedIn) return;
    if (searchParams.get("checkout") !== "1") return;
    let cancelled = false;
    (async () => {
      setSyncing(true);
      setMsg("Synchronisation de votre abonnement…");
      try {
        const sync = await api.post("/api/billing/sync").catch(() => null);
        await api.post("/api/promos/confirm").catch(() => null);
        if (!cancelled) {
          await refresh();
          if (sync?.promo?.ok && sync.promo.code) {
            setMsg(
              `Premium activé${
                sync.promo.channel ? ` (via ${sync.promo.channel})` : ""
              }.`
            );
          } else {
            setMsg("");
          }
        }
      } finally {
        if (!cancelled) setSyncing(false);
        const next = new URLSearchParams(searchParams);
        next.delete("checkout");
        next.delete("plan_id");
        setSearchParams(next, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, searchParams, setSearchParams, refresh]);

  const price = billing?.price_label || me?.price_label || "15 $ / mois";
  const plan = (me?.plan || billing?.plan || "freemium").toLowerCase();
  const premiumPlanId = billing?.premium_plan?.id || null;
  const promoPlanId = billing?.promo_plan?.id || null;
  const usePromoCheckout = Boolean(promo?.first_month_free && promoPlanId);
  const planId = usePromoCheckout ? promoPlanId : premiumPlanId;
  const billingEnabled = billing?.billing_enabled === true;

  const applyPromo = async (e) => {
    e?.preventDefault?.();
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    setPromoError("");
    setMsg("");
    try {
      const res = await api.post("/api/promos/reserve", {
        code: promoInput.trim().toUpperCase(),
      });
      setPromo(res);
      setMsg(res.detail || "Code promo appliqué.");
      const next = new URLSearchParams(searchParams);
      next.set("promo", res.code);
      setSearchParams(next, { replace: true });
    } catch (err) {
      setPromo(null);
      setPromoError(err.message || "Code invalide");
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = async () => {
    setPromoBusy(true);
    try {
      await api.post("/api/promos/clear").catch(() => null);
      setPromo(null);
      setPromoInput("");
      setMsg("");
      const next = new URLSearchParams(searchParams);
      next.delete("promo");
      setSearchParams(next, { replace: true });
    } finally {
      setPromoBusy(false);
    }
  };

  const onSubscriptionComplete = async () => {
    setSyncing(true);
    setMsg(
      promo?.first_month_free
        ? "Carte enregistrée — activation de votre mois offert…"
        : "Paiement reçu — activation Premium…"
    );
    try {
      await api.post("/api/billing/sync").catch(() => null);
      await api.post("/api/promos/confirm").catch(() => null);
      await refresh();
      setMsg(
        promo?.first_month_free
          ? "Mois offert activé. Résiliez avant la fin de l’essai pour ne pas être débité."
          : "Premium activé. Vous pouvez utiliser tous les modes."
      );
    } catch (e) {
      setMsg(
        e.message ||
          "Paiement OK, synchronisation en cours — rechargez dans quelques secondes."
      );
    } finally {
      setSyncing(false);
    }
  };

  const ctaLabel = syncing
    ? "Activation…"
    : usePromoCheckout
      ? `Essayer Premium — 1er mois offert`
      : `Passer Premium — ${price}`;

  const content = (
    <main className={embedded ? "max-w-4xl" : "max-w-4xl mx-auto px-4 py-12"}>
      <div className="text-center mb-10">
        <h1 className="text-3xl font-display font-bold text-ink tracking-tight">
          Tarifs
        </h1>
        <p className="text-mute mt-2 text-sm">
          Lancez votre activation LinkedIn. Premium à prix early-bird.
        </p>
        {plan === "premium" && (
          <p className="mt-3 inline-flex text-xs font-semibold uppercase tracking-wide bg-ink text-cream px-3 py-1 rounded-md">
            Votre plan actuel · Premium
          </p>
        )}
      </div>

      <SignedIn>
        {plan !== "premium" && (
          <form
            onSubmit={applyPromo}
            className="mb-8 p-4 border border-line bg-paper/80 rounded-xl flex flex-col sm:flex-row gap-3 sm:items-end"
          >
            <div className="flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-mute mb-1">
                Code promo
              </label>
              <input
                type="text"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder="Ex. MV-LI-XXXX"
                className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
                disabled={promoBusy}
                autoComplete="off"
              />
              {promoError && (
                <p className="mt-1 text-xs text-rouge">{promoError}</p>
              )}
              {promo?.ok && (
                <p className="mt-1 text-xs text-emerald-700">
                  {promo.detail}
                  {promo.channel ? ` · canal ${promo.channel}` : ""}
                  {promo.first_month_free && !promoPlanId
                    ? " · (plan essai Clerk à configurer — attribution OK)"
                    : ""}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={promoBusy || !promoInput.trim()}
                className="btn-secondary disabled:opacity-50"
              >
                Appliquer
              </button>
              {promo?.ok && (
                <button
                  type="button"
                  onClick={clearPromo}
                  disabled={promoBusy}
                  className="btn-secondary disabled:opacity-50"
                >
                  Retirer
                </button>
              )}
            </div>
          </form>
        )}
      </SignedIn>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card p-8 border border-line">
          <p className="type-label-accent">Freemium</p>
          <p className="text-3xl font-display font-bold text-ink mt-2">0 $</p>
          <p className="text-sm text-mute mt-1">Pour tester</p>
          <ul className="mt-6 space-y-2 text-sm text-ink/70">
            <li>1 segment</li>
            <li>5 actions / jour</li>
            <li>Modes Visite + Connexion</li>
            <li>Envoi auto &amp; recherche auto</li>
            <li>
              Pause tous les 3 jours : autos désactivés + LinkedIn déconnecté
              (à reconnecter)
            </li>
            <li>Templates intelligents</li>
          </ul>
          <SignedOut>
            <Link to="/register" className="btn-secondary mt-8 w-full text-center block">
              Créer un compte
            </Link>
          </SignedOut>
          <SignedIn>
            <Link to="/" className="btn-secondary mt-8 w-full text-center block">
              Continuer en Freemium
            </Link>
          </SignedIn>
        </div>

        <div className="card p-8 border-2 border-rouge relative">
          <p className="absolute -top-3 right-4 text-[10px] font-semibold uppercase tracking-wide bg-rouge text-white px-2 py-0.5 rounded">
            Early bird
          </p>
          <p className="type-label-accent text-rouge">Premium</p>
          <p className="text-3xl font-display font-bold text-ink mt-2">
            {usePromoCheckout ? (
              <>
                <span className="line-through text-mute text-xl mr-2">15 $</span>
                0 $
                <span className="text-base font-sans font-normal text-mute">
                  {" "}
                  le 1er mois
                </span>
              </>
            ) : (
              price
            )}
          </p>
          <p className="text-sm text-mute mt-1">
            {usePromoCheckout
              ? `Puis ${price} — carte requise, résiliable avant la fin de l’essai`
              : "Sans engagement · paiement sécurisé via Clerk / Stripe"}
          </p>
          <ul className="mt-6 space-y-2 text-sm text-ink/70">
            <li>Jusqu’à 50 segments</li>
            <li>30 actions / jour</li>
            <li>Tous les modes (Visite, Connexion, Invitation, Message)</li>
            <li>Envoi auto &amp; recherche auto en continu</li>
            <li>LinkedIn reste connecté (pas de pause 3 jours)</li>
            {usePromoCheckout && (
              <li className="text-rouge font-medium">
                Code {promo.code} · {promo.trial_days} jours offerts
              </li>
            )}
          </ul>
          <SignedOut>
            <Link
              to={`/register${
                promoInput ? `?redirect=/pricing&promo=${encodeURIComponent(promoInput)}` : ""
              }`}
              className="btn-primary mt-8 w-full text-center block"
            >
              S’inscrire puis upgrader
            </Link>
          </SignedOut>
          <SignedIn>
            {plan === "premium" ? (
              <div className="mt-8 space-y-3">
                <button type="button" disabled className="btn-primary w-full opacity-50">
                  Déjà Premium
                </button>
                <SubscriptionDetailsButton>
                  <button type="button" className="btn-secondary w-full">
                    Gérer l’abonnement
                  </button>
                </SubscriptionDetailsButton>
              </div>
            ) : !billingEnabled || !planId ? (
              <button
                type="button"
                disabled
                className="btn-primary mt-8 w-full opacity-50"
              >
                Paiement en configuration…
              </button>
            ) : (
              <div className="mt-8">
                <CheckoutButton
                  planId={planId}
                  planPeriod="month"
                  for="user"
                  newSubscriptionRedirectUrl="/pricing?checkout=1"
                  onSubscriptionComplete={onSubscriptionComplete}
                  checkoutProps={{ appearance: checkoutAppearance }}
                >
                  <button
                    type="button"
                    disabled={syncing}
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {ctaLabel}
                  </button>
                </CheckoutButton>
              </div>
            )}
          </SignedIn>
        </div>
      </div>

      {msg && (
        <p className="mt-6 text-sm text-center text-ink/70 bg-paper border border-line rounded-lg p-4">
          {msg}
        </p>
      )}

      <SignedIn>
        {!billingEnabled && (
          <p className="mt-4 text-center text-xs text-rouge">
            Billing Clerk pas encore prêt côté instance. Vérifiez Stripe / plan Premium dans le Dashboard.
          </p>
        )}
      </SignedIn>

      <p className="mt-10 text-center text-xs text-mute">
        En utilisant MmeValentine vous acceptez les{" "}
        <Link to="/cgu" className="underline">
          CGU
        </Link>{" "}
        et la{" "}
        <Link to="/confidentialite" className="underline">
          politique de confidentialité
        </Link>
        .
      </p>
    </main>
  );

  if (embedded) {
    return <div className="space-y-2">{content}</div>;
  }

  return (
    <div className="min-h-screen bg-cream font-sans">
      <header className="h-16 border-b border-line bg-paper/80 backdrop-blur flex items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-rouge rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm font-display">V</span>
          </div>
          <span className="font-display font-semibold text-ink">MmeValentine</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <SignedOut>
            <Link to="/login" className="text-ink/60 hover:text-ink">
              Connexion
            </Link>
          </SignedOut>
          <SignedIn>
            <Link to="/" className="text-ink/60 hover:text-ink">
              Tableau de bord
            </Link>
          </SignedIn>
        </div>
      </header>
      {content}
    </div>
  );
}
